/**
 * useDjBillboard — the radio VIDEO HOST cameo, ported from the website's
 * useVideoHost to HypeTunes_World. The station's DJ steps onto the big city
 * billboards as short CAMEOS for DYNAMIC moments, speaks a live line, then
 * steps off — the billboard art returns underneath.
 *
 * WHY dynamic-only: SCHEDULED narrations are deliberately AUDIO-ONLY. Their mp3
 * durations ARE the deterministic playlist offsets every listener's playhead is
 * computed from; a live on-camera retelling has a different duration per viewer
 * and drifts the World's playhead off the shared station clock (and off website
 * listeners). This was tried and reverted 2026-07-17 — see useVideoHost.js. So
 * the World only cameos on dynamic drops the schedule doesn't depend on.
 *
 * The Anam stream binds to a hidden <video id="dj-cameo-video"> (audio plays;
 * pixels are sampled into a VideoTexture on the billboards by CityBillboards).
 * The station music ducks under the DJ and comes back when they step off.
 * One-way session (disableInputAudio) — the DJ talks, nobody talks back.
 *
 * Server enforces access (paid plan) + the appearance budget (a 429 = "stay on
 * audio, back off"). Admin bypasses both, so it's testable.
 */
import { useCallback, useEffect, useRef } from 'react';
import { api } from '../services/api';
import { audioEngine } from '../services/audioEngine';
import { useWorldStore } from '../state/worldStore';

const VIDEO_EL_ID = 'dj-cameo-video';
const EXIT_GRACE_MS = 2500;          // step off this soon after a line finishes
const DEFAULT_BACKOFF_MS = 5 * 60_000;
const ACTIVITY_WINDOW_MS = 10 * 60_000;   // no auto-cameos for an idle tab
const FIRST_DELAY_MS = 25_000;       // settle into the world before the first drop
const AUTO_EVERY_MS = 4 * 60_000;    // cadence of dynamic drops while present

const speechMs = (text) => {
  const words = String(text || '').trim().split(/\s+/).filter(Boolean).length;
  return Math.min(30000, Math.max(1800, Math.round((words / 165) * 60000) + 800));
};

export default function useDjBillboard() {
  const setDjCameo = useWorldStore((s) => s.setDjCameo);

  const phaseRef = useRef('idle');            // idle | armed | connecting | live
  const stationIdRef = useRef(null);
  const clientRef = useRef(null);
  const sessionRef = useRef(null);
  const endedRef = useRef(false);
  const liveRef = useRef(false);
  const runRef = useRef(0);                   // cancellation token for openCameo
  const aliveRef = useRef(true);
  const backoffRef = useRef(0);
  const lastActivityRef = useRef(Date.now());
  const lingerTimerRef = useRef(null);
  const capTimerRef = useRef(null);
  const talkTimerRef = useRef(null);

  const setPhase = useCallback((p, extra = {}) => {
    phaseRef.current = p;
    if (aliveRef.current) setDjCameo({ phase: p, ...extra });
  }, [setDjCameo]);

  const report = useCallback(async (body) => {
    const sid = sessionRef.current;
    if (!sid) return;
    try { await api.anamSessionReport(sid, body); } catch { /* best effort */ }
  }, []);

  const clearTimers = () => {
    [lingerTimerRef, capTimerRef, talkTimerRef].forEach((r) => { if (r.current) { clearTimeout(r.current); r.current = null; } });
  };

  const closeCameo = useCallback((next = 'armed') => {
    runRef.current += 1;                       // supersede any in-flight connect
    clearTimers();
    try { clientRef.current?.stopStreaming?.(); } catch { /* noop */ }
    clientRef.current = null;
    liveRef.current = false;
    audioEngine.duck(false);                   // release the music duck
    if (!endedRef.current && sessionRef.current) { endedRef.current = true; report({ event: 'ended' }); }
    sessionRef.current = null;
    setPhase(next === 'idle' ? 'idle' : next, { videoEl: null });
  }, [report, setPhase]);
  const closeRef = useRef(closeCameo);
  useEffect(() => { closeRef.current = closeCameo; }, [closeCameo]);

  const armLinger = useCallback((extraMs = 0) => {
    if (lingerTimerRef.current) clearTimeout(lingerTimerRef.current);
    lingerTimerRef.current = setTimeout(() => closeRef.current?.('armed'), extraMs + EXIT_GRACE_MS);
  }, []);

  const speak = useCallback((text) => {
    const client = clientRef.current;
    const line = String(text || '').trim();
    if (!client || !liveRef.current || !line) return;
    try {
      const t = client.talk(line);
      if (t?.catch) t.catch((e) => console.warn('[DjBillboard] talk rejected:', e?.message || e));
      audioEngine.duck(true);                  // dip the station under the DJ
      if (talkTimerRef.current) clearTimeout(talkTimerRef.current);
      talkTimerRef.current = setTimeout(() => audioEngine.duck(false), speechMs(line));
      armLinger(speechMs(line));
    } catch { /* line lost */ }
  }, [armLinger]);

  const openCameo = useCallback(async () => {
    const stationId = stationIdRef.current;
    if (phaseRef.current !== 'armed' || !stationId) return;
    if (Date.now() < backoffRef.current) return;
    if (Date.now() - lastActivityRef.current > ACTIVITY_WINDOW_MS) return; // idle tab, cost care

    const run = ++runRef.current;
    const cancelled = () => run !== runRef.current || !aliveRef.current;
    setPhase('connecting');
    let client = null;
    try {
      const mod = await import(/* @vite-ignore */ 'https://esm.sh/@anam-ai/js-sdk@latest');
      if (cancelled()) throw Object.assign(new Error('cancelled'), { cancelled: true });
      const createClient = mod.createClient || mod.default?.createClient;
      const AnamEvent = mod.AnamEvent || mod.default?.AnamEvent || {};
      if (!createClient) throw new Error('engine load failed');

      const { res, data } = await api.anamRadioSessionToken(stationId);
      if (!res.ok || !data.success) {
        if (res.status === 429) backoffRef.current = Date.now() + ((data.retryAfterSec || 300) * 1000);
        else if (res.status === 503) backoffRef.current = Date.now() + DEFAULT_BACKOFF_MS;
        const err = new Error(data.error || `cameo denied (${res.status})`);
        err.quiet = res.status === 429 || res.status === 503;
        throw err;
      }
      sessionRef.current = data.sessionId;
      endedRef.current = false;
      if (cancelled()) throw Object.assign(new Error('cancelled'), { cancelled: true });

      client = createClient(data.sessionToken, { disableInputAudio: true });
      clientRef.current = client;

      let readyResolve;
      const sessionReady = new Promise((resolve) => { readyResolve = resolve; });
      try {
        if (AnamEvent.SESSION_READY) client.addListener(AnamEvent.SESSION_READY, (sid) => {
          report({ anamSessionId: sid, event: 'started' });   // powers the server stop-backstop
          readyResolve(true);
        });
        if (AnamEvent.CONNECTION_CLOSED) client.addListener(AnamEvent.CONNECTION_CLOSED, () => {
          if (liveRef.current) closeRef.current?.('armed');
        });
        const onSpeechDone = () => { audioEngine.duck(false); armLinger(); };
        if (AnamEvent.MESSAGE_HISTORY_UPDATED) client.addListener(AnamEvent.MESSAGE_HISTORY_UPDATED, onSpeechDone);
        if (AnamEvent.TALK_STREAM_INTERRUPTED) client.addListener(AnamEvent.TALK_STREAM_INTERRUPTED, onSpeechDone);
      } catch { /* listeners best-effort */ }

      const videoEl = document.getElementById(VIDEO_EL_ID);
      if (!videoEl) throw Object.assign(new Error('cancelled'), { cancelled: true });
      await client.streamToVideoElement(VIDEO_EL_ID);
      if (cancelled()) throw Object.assign(new Error('cancelled'), { cancelled: true });

      if (AnamEvent.SESSION_READY) {
        const ready = await Promise.race([sessionReady, new Promise((r) => setTimeout(() => r(false), 10000))]);
        if (!ready) throw new Error('session never became ready');
      } else {
        await new Promise((r) => setTimeout(r, 1500));
      }
      if (cancelled()) throw Object.assign(new Error('cancelled'), { cancelled: true });

      liveRef.current = true;
      setPhase('live', { hostName: data.hostName || null, videoEl });
      capTimerRef.current = setTimeout(() => closeRef.current?.('armed'), Math.max(30, (data.maxSeconds || 120) - 5) * 1000);
      armLinger();

      // Ask the brain for an unprompted DJ drop (same persona/thread as chat),
      // then speak it on camera. greeting:true → a self-started line.
      try {
        const turn = await api.anamSessionTurn(sessionRef.current, { greeting: true });
        if (!cancelled() && turn?.data?.reply) speak(turn.data.reply);
        else armLinger();
      } catch { armLinger(); }
    } catch (err) {
      if (err.cancelled) console.info('[DjBillboard] cameo cancelled mid-connect');
      else if (err.quiet) console.info('[DjBillboard] cameo skipped:', err.message);
      else console.warn('[DjBillboard] cameo failed:', err.message);
      audioEngine.duck(false);
      try { (client || clientRef.current)?.stopStreaming?.(); } catch { /* noop */ }
      if (clientRef.current === client) clientRef.current = null;
      if (sessionRef.current && !endedRef.current) { endedRef.current = true; report({ event: 'ended' }); }
      sessionRef.current = null;
      setPhase(phaseRef.current === 'idle' ? 'idle' : 'armed', { videoEl: null });
    }
  }, [setPhase, report, armLinger, speak]);

  // Resolve the station id from the now-playing payload, then check whether the
  // host is on-camera-capable + this viewer may watch → auto-arm (admin/paid).
  useEffect(() => {
    aliveRef.current = true;
    let dead = false;
    const unsub = useWorldStore.subscribe((st) => {
      const id = st.nowPlaying?.station?.station_id;
      if (id && id !== stationIdRef.current) {
        stationIdRef.current = id;
        api.anamRadioStatus(id).then((d) => {
          if (dead || !d?.success || !d.available || !d.planOk) return;
          if (phaseRef.current === 'idle') setPhase('armed', { hostName: d.hostName || null });
        }).catch(() => { /* stays idle */ });
      }
    });
    // Prime from current state (subscribe only fires on change).
    const id0 = useWorldStore.getState().nowPlaying?.station?.station_id;
    if (id0) {
      stationIdRef.current = id0;
      api.anamRadioStatus(id0).then((d) => {
        if (dead || !d?.success || !d.available || !d.planOk) return;
        if (phaseRef.current === 'idle') setPhase('armed', { hostName: d.hostName || null });
      }).catch(() => {});
    }
    return () => { dead = true; unsub(); };
  }, [setPhase]);

  // Activity clock (auto-cameos pause for an idle tab).
  useEffect(() => {
    const mark = () => { lastActivityRef.current = Date.now(); };
    window.addEventListener('pointerdown', mark, { capture: true, passive: true });
    window.addEventListener('keydown', mark, { capture: true, passive: true });
    return () => {
      window.removeEventListener('pointerdown', mark, { capture: true });
      window.removeEventListener('keydown', mark, { capture: true });
    };
  }, []);

  // Dynamic-moment scheduler: a first drop after the world settles, then a
  // steady cadence while present. Dev: press "v" to fire one now (?dj shows chrome).
  useEffect(() => {
    const first = setTimeout(() => { if (phaseRef.current === 'armed') openCameo(); }, FIRST_DELAY_MS);
    const iv = setInterval(() => { if (phaseRef.current === 'armed') openCameo(); }, AUTO_EVERY_MS);
    const onKey = (e) => { if (e.key === 'v' && phaseRef.current === 'armed') openCameo(); };
    window.addEventListener('keydown', onKey);
    return () => { clearTimeout(first); clearInterval(iv); window.removeEventListener('keydown', onKey); };
  }, [openCameo]);

  // Teardown: close any open cameo (reports ended → server stops Anam + refunds
  // the unused block) on unmount and on hard page exit.
  useEffect(() => {
    const flush = () => {
      const sid = sessionRef.current;
      if (!sid || endedRef.current) return;
      endedRef.current = true;
      try { api.anamSessionReport(sid, { event: 'ended' }); } catch { /* noop */ }
    };
    window.addEventListener('pagehide', flush);
    return () => {
      aliveRef.current = false;
      window.removeEventListener('pagehide', flush);
      closeRef.current?.('idle');
    };
  }, []);

  return { trigger: openCameo };
}
